import type { ReactNode } from 'react';

export type BadgeTone = 'neutral' | 'success' | 'warning' | 'info' | 'danger';
export type BadgeVariant = 'pill' | 'tag';

export interface BadgeProps {
  /** Explicit tone. Ignored when `status` resolves a tone. */
  tone?: BadgeTone;
  /**
   * Raw status string (active/paused/suspended/draft/...). Mapped to a tone,
   * absorbing the platform's old `statusBadgeClass()` helper. When set and no
   * children are given, the status text is used as the label.
   */
  status?: string;
  variant?: BadgeVariant;
  className?: string;
  /** Optional native tooltip, e.g. explaining what a status means. */
  title?: string;
  children?: ReactNode;
}

/**
 * Map a raw tenant/newsletter status to a Badge tone. Replaces the platform's
 * ad-hoc `statusBadgeClass()` switch.
 */
export function statusToTone(status: string): BadgeTone {
  switch (status.toLowerCase()) {
    case 'active':
    case 'live':
      return 'success';
    case 'paused':
    case 'pending':
      return 'warning';
    case 'suspended':
    case 'cancelled':
    case 'canceled':
      return 'danger';
    case 'draft':
    default:
      return 'neutral';
  }
}

export function Badge({ tone, status, variant = 'pill', className, title, children }: BadgeProps) {
  const resolvedTone: BadgeTone = status !== undefined ? statusToTone(status) : (tone ?? 'neutral');
  const classes = ['nuff-badge', `nuff-badge--${resolvedTone}`, `nuff-badge--${variant}`, className]
    .filter(Boolean)
    .join(' ');

  const content = children ?? status ?? null;

  return (
    <span className={classes} title={title}>
      {content}
    </span>
  );
}
